import React, { useEffect, useState, useCallback } from "react";
import { errorMessage } from "../../services";
import { getSiteSettings, getSites, updateSiteSettings } from "../../services/sites";
import { SETUP_CHANGED_EVENT } from "../../utils/support";
import Loader, { Spinner } from "../../components/Loader";
import SearchSelect from "../../components/SearchSelect";
import PasswordInput from "../../components/PasswordInput";

// Each card saves on its own, so saving one section never counts as reviewing another (see the setup checklist).
// `id` is the anchor the checklist links to (#store-payment ...).
const GROUPS = [
  { id: "store-business", title: "Business information", desc: "Your business identity and primary contact details.", icon: "▣", fields: [["name", "Full name"], ["storeName", "Store name"], ["email", "Email address"], ["contact", "Contact number"], ["address", "Business address"]] },
  { id: "store-shipping", title: "Shipping & tax", desc: "The delivery fee, the order amount above which shipping is free (₹0), and the GST rate used at checkout.", icon: "↗", fields: [["shipping", "Shipping charge (₹)"], ["freeShippingAbove", "Free shipping above (₹)"], ["gst", "GST (%)"]] },
  {
    id: "store-payment",
    title: "UPI payments (Razorpay)",
    desc: "Shoppers pay by UPI (PhonePe, Google Pay, any UPI app or QR code) into your own Razorpay account, and Razorpay confirms each payment, so you never check or mark orders paid. Razorpay's fees apply.",
    icon: "₹",
    fields: [["paymentMode", "Status"], ["paymentKey", "Key ID"], ["paymentSecret", "Key secret"]],
  },
  {
    id: "store-manual-upi",
    title: "Manual UPI payments",
    desc: "Shoppers pay your own UPI ID by QR code or a UPI app, then send their order details to your WhatsApp. You check the payment and tap \"Mark as paid\" in Manage orders. No gateway and no fees. Leave the UPI ID empty to switch it off.",
    icon: "📲",
    fields: [["upiId", "Your UPI ID"], ["upiName", "Name shown in the UPI app"], ["orderWhatsapp", "WhatsApp number for orders"]],
  },
  {
    id: "store-courier",
    title: "Courier (Shiprocket)",
    desc: "Book Delhivery, Bluedart, Ekart, Xpressbees and more from Manage orders. Use your own Shiprocket account: you pay Shiprocket directly for each shipment.",
    icon: "🚚",
    fields: [
      ["courierProvider", "Courier service"], ["courierEmail", "Shiprocket API user email"], ["courierPassword", "Shiprocket API user password"],
      ["courierPickup", "Pickup address name (as in Shiprocket)"], ["courierPickupPincode", "Pickup pincode"],
      ["parcelWeight", "Default parcel weight (kg)"], ["parcelLength", "Length (cm)"], ["parcelBreadth", "Breadth (cm)"], ["parcelHeight", "Height (cm)"],
    ],
  },
  {
    id: "store-gifting",
    title: "Gift options",
    desc: "What shoppers can add at checkout: gift wrap and a greeting card for these prices (₹0 = free), and a delivery date they pick, at least this many days ahead (0 = today). You see their choices on the order.",
    icon: "🎁",
    template: "gifts", // shown for gift stores only
    fields: [["giftWrap", "Gift wrap"], ["giftWrapPrice", "Gift wrap price (₹)"], ["giftCard", "Greeting card"], ["giftCardPrice", "Greeting card price (₹)"], ["deliveryDate", "Shopper picks a delivery date"], ["deliveryLeadDays", "Earliest delivery (days from today)"]],
  },
];
const NUMBER_FIELDS = ["shipping", "freeShippingAbove", "gst", "parcelWeight", "parcelLength", "parcelBreadth", "parcelHeight", "giftWrapPrice", "giftCardPrice", "deliveryLeadDays"];
const ON_OFF_FIELDS = ["giftWrap", "giftCard", "deliveryDate"]; // shown as On / Off, saved as true / false
const SECRET_FIELDS = ["courierPassword", "paymentSecret"]; // blank keeps the saved value

// How shoppers pay right now (settings.onlinePayment from the server, services/storePayments) and what the owner
// should know about it. `keys`: the owner's Razorpay keys are saved; `testKeys`: they are rzp_test_ keys.
function paymentStatusNote({ onlinePayment, keys, testKeys }) {
  if (onlinePayment === "demo") {
    return { ok: true, text: `This is an Ownezzy demo store, so shoppers' UPI payments are demo payments: no real money is charged. ${keys ? "Your Razorpay keys are saved." : "Add your Razorpay keys to take live payments."}` };
  }
  if (onlinePayment === "live") {
    return testKeys
      ? { ok: false, text: "These are Razorpay TEST keys (rzp_test_…): shoppers' payments are not real money. Save your live keys (rzp_live_…) to take real payments." }
      : { ok: true, text: "✓ UPI payments go to your Razorpay account and are confirmed automatically." };
  }
  return { ok: false, text: "Cash on delivery only. Add your Razorpay keys to take UPI payments." };
}

// Per-website settings (business details, shipping, GST, Razorpay), for a store owner (Store settings tab)
// and a Super Admin (Store settings tab).
// - siteId: edit this one website, with no website picker (the website editor's Shipping & tax tab)
// - only: show just these cards, e.g. ["store-shipping"]
// - allWebsites: the Super Admin's tab, which always shows the website picker

const siteOption = (site) => ({ value: site.id, label: site.name, hint: site.domain || site.slug, site });
export default function StoreSettingsForm({ notify, siteId: fixedSiteId, only, allWebsites = false }) {
  const [sites, setSites] = useState([]);
  const [listLoaded, setListLoaded] = useState(Boolean(fixedSiteId));
  const [siteId, setSiteId] = useState(fixedSiteId || "");
  const [selectedSite, setSelectedSite] = useState(null);
  const [form, setForm] = useState(null);
  const [courierPasswordSet, setCourierPasswordSet] = useState(false);
  const [payment, setPayment] = useState({ onlinePayment: "", keys: false, testKeys: false }); // as saved, not as typed
  const [paymentSecretSet, setPaymentSecretSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(""); // id of the card being saved
  // Cards for one store type (Gift options) show only for that type, once the settings have loaded
  const groups = (only ? GROUPS.filter((g) => only.includes(g.id)) : GROUPS).filter((g) => !g.template || (form && form.template === g.template));

  // The websites this account can manage, by name (the API's page limit: the first 100)
  useEffect(() => {
    if (fixedSiteId) return undefined;
    let live = true;
    getSites({ limit: 100, sort: "name", demo: "include" })
      .then(({ data }) => {
        if (!live) return;
        setSites(data.sites);
        setListLoaded(true);
        if (!data.sites.length) setLoading(false);
        setSelectedSite(data.sites[0] || null);
        setSiteId(data.sites[0] ? data.sites[0].id : "");
      })
      .catch((err) => { if (live) { setError(errorMessage(err)); setListLoaded(true); setLoading(false); } });
    return () => { live = false; };
  }, [fixedSiteId]);

  // The picker searches every website this account can manage, demo stores included (not just the first 100)
  const searchSites = useCallback(
    (query) => getSites({ search: query, limit: 20, sort: "name", demo: "include" }).then(({ data }) => data.sites.map(siteOption)),
    []
  );
  const siteName = (fixedSiteId ? null : selectedSite && selectedSite.name) || "this website";
  const showPicker = !fixedSiteId && (allWebsites || sites.length > 1);

  useEffect(() => {
    if (!siteId) return;
    let live = true;
    setLoading(true);
    setForm(null); // another website's values must never show under this one's name
    getSiteSettings(siteId)
      .then(({ data }) => {
        if (!live) return;
        const { paymentSecretSet: secretSaved, courierPasswordSet: courierSaved, setup, ...rest } = data.settings;
        setForm({ ...rest, courierPassword: "", paymentSecret: "" });
        setCourierPasswordSet(courierSaved);
        rememberPayment(rest, secretSaved);
        setError("");
      })
      .catch((err) => live && setError(errorMessage(err)))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [siteId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // How shoppers can pay, as saved on the server (not as typed)
  const rememberPayment = (settings, secretSaved) => {
    setPaymentSecretSet(secretSaved);
    setPayment({
      onlinePayment: settings.onlinePayment || "",
      keys: Boolean(settings.paymentMode === "Razorpay" && settings.paymentKey && secretSaved),
      testKeys: Boolean(settings.paymentTestMode),
    });
  };

  // The setup checklist links here as #store-payment etc.: open that card once the form has loaded
  const formLoaded = Boolean(form);
  useEffect(() => {
    if (!formLoaded) return;
    const card = groups.find((g) => `#${g.id}` === window.location.hash);
    if (card) document.getElementById(card.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [formLoaded]);

  // Saves only this card's fields
  const submit = (group) => async (e) => {
    e.preventDefault();
    setBusy(group.id);
    try {
      const payload = {};
      group.fields.forEach(([key]) => { payload[key] = NUMBER_FIELDS.includes(key) ? Number(form[key]) : ON_OFF_FIELDS.includes(key) ? Boolean(form[key]) : form[key]; });
      SECRET_FIELDS.forEach((key) => { if (!payload[key]) delete payload[key]; }); // blank keeps the saved secret
      const { data } = await updateSiteSettings(siteId, payload);
      const { paymentSecretSet: secretSaved, courierPasswordSet: courierSaved, setup, ...rest } = data.settings;
      setForm((f) => ({ ...f, ...rest, courierPassword: "", paymentSecret: "" }));
      setCourierPasswordSet(courierSaved);
      rememberPayment(rest, secretSaved);
      window.dispatchEvent(new Event(SETUP_CHANGED_EVENT));
      notify(`${group.title} saved`);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy("");
    }
  };

  const renderField = ([key, label]) => (
    <label key={key} className={`admin-field ${key === "address" ? "wide-field" : ""}`}>
      {label}
      {key === "paymentMode" ? (
        <select value={form.paymentMode === "Razorpay" ? "Razorpay" : "Manual / COD only"} onChange={set(key)}>
          <option value="Manual / COD only">Off</option>
          <option value="Razorpay">On (Razorpay)</option>
        </select>
      ) : ON_OFF_FIELDS.includes(key) ? (
        <select value={form[key] ? "on" : "off"} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value === "on" }))}>
          <option value="on">On</option>
          <option value="off">Off</option>
        </select>
      ) : key === "courierProvider" ? (
        <select value={form.courierProvider || ""} onChange={set(key)}>
          <option value="">Not connected</option>
          <option value="shiprocket">Shiprocket</option>
        </select>
      ) : SECRET_FIELDS.includes(key) ? (
        <PasswordInput
          autoComplete="new-password"
          value={form[key] ?? ""}
          onChange={set(key)}
          placeholder={key === "paymentSecret" ? (paymentSecretSet ? "•••••••• saved (leave blank to keep)" : "Shown once when you generate the key")
            : courierPasswordSet ? "•••••••• saved (leave blank to keep)" : "Enter the API user password"}
        />
      ) : (
        <input
          type={["email", "courierEmail"].includes(key) ? "email" : NUMBER_FIELDS.includes(key) ? "number" : "text"}
          min={NUMBER_FIELDS.includes(key) ? 0 : undefined}
          step={["gst", "parcelWeight"].includes(key) ? "any" : undefined}
          autoComplete="off"
          value={form[key] ?? ""}
          onChange={set(key)}
          placeholder={{ paymentKey: "rzp_test_… or rzp_live_…", courierPickup: "e.g. Primary", courierEmail: "api-user@yourshop.com", upiId: "e.g. yourshop@okaxis", upiName: "e.g. your shop's name", orderWhatsapp: "10-digit number (empty: your contact number)" }[key] || `Enter ${label.toLowerCase()}`}
        />
      )}
    </label>
  );

  if (!siteId && !listLoaded) return <Loader label="Loading store settings" />;
  if (!siteId && !loading) return error ? <div className="admin-error">{error}</div> : <div className="panel admin-empty">{allWebsites ? "There are no websites yet." : "You don't have a website yet, so there are no store settings."}</div>;

  return (
    <div className={fixedSiteId ? "settings-form settings-form-embedded" : "settings-form"}>
      {/* The Super Admin's Store settings tab has its own page heading */}
      {!fixedSiteId && !allWebsites && (
        <div className="settings-intro">
          <div className="settings-intro-icon">⚙</div>
          <div>
            <h2>Store settings</h2>
            <p>These details are used at your storefront's checkout and contact page.</p>
          </div>
        </div>
      )}
      {showPicker && (
        <div className="settings-site-picker">
          <SearchSelect
            label="Website you are updating"
            placeholder="Search by store name or address…"
            emptyText="No website matches"
            selected={selectedSite && siteOption(selectedSite)}
            loadOptions={searchSites}
            onChange={(option) => { setSelectedSite(option.site); setSiteId(option.value); }}
          />
        </div>
      )}
      {error && <div className="admin-error">{error}</div>}
      {loading && !form && <Loader label="Loading store settings" />}
      {form && (
        <>
          <div className="settings-sections">
            {groups.map((group) => (
              <form className="settings-card" id={group.id} key={group.id} onSubmit={submit(group)}>
                <div className="settings-card-heading"><span className="settings-card-icon">{group.icon}</span><div><h3>{group.title}</h3><p>{group.desc}</p>{showPicker && <span className="settings-card-site">For {siteName}</span>}</div></div>
                {group.id === "store-courier" && (
                  <div className="settings-courier-help">
                    <b>How to connect:</b> in Shiprocket open <em>Settings → API → Configure</em>, create an API user (a different email from your login) and enter it here. Add your pickup address in Shiprocket and type its name exactly as saved there.
                  </div>
                )}
                {group.id === "store-payment" && (() => {
                  const note = paymentStatusNote(payment);
                  return <div className={note.ok ? "settings-upi-on" : "settings-cod-note"}>{note.text}</div>;
                })()}
                <div className="settings-fields-grid">{group.fields.map(renderField)}</div>
                {group.id === "store-payment" && (
                  <div className="settings-courier-help">
                    <b>How to connect:</b> sign up at <em>razorpay.com</em>. In the Razorpay Dashboard open <em>Account &amp; Settings → API Keys</em>, generate a key and copy the Key ID and Key secret here. Use your live keys (<em>rzp_live_…</em>), which Razorpay gives after its KYC. The keys are checked with Razorpay when you save.
                  </div>
                )}
                <div className="settings-card-footer">
                  <button className="primary-btn" disabled={Boolean(busy)}>{busy === group.id ? <><Spinner /> Saving</> : `✓ Save ${/^[A-Z]{2}/.test(group.title) ? group.title : group.title.charAt(0).toLowerCase() + group.title.slice(1)}`}</button>
                </div>
              </form>
            ))}
          </div>
          {!fixedSiteId && <p className="settings-applies">Changes apply to {siteName} only.</p>}
        </>
      )}
    </div>
  );
}
